import { call, select, put } from 'redux-saga/effects';
import { WEB_SERVICES } from '../bitSagas/webServices';
import { updateMyContacts as updateMyContactsAda } from '../bitSagas/updateMyContacts';
import { updatePermanentResidence as updatePermanentResidenceAda } from '../bitSagas/updatePermanentResidence';
import { getClientData as getClientDataAda } from '../bitSagas/getClientData';
import { getMyDataType as getMyDataTypeAda } from '../bitSagas/getMyDataType';
import { megaMinderStatus } from '../minder/MegaMinder/megaMinderStatus';
import { commonActions } from '../reducers/common';
import { actions as uiNeedsActions } from '../reducers/uiNeeds';
import { DOC_TYPE_CIF, USER_TYPE } from '../bitConstants/constants';

const apisNeedsStore = state => state.apiNeeds;
const contextStore = state => state.context.data;
const getErrorManagement = state => state.errorManagement;
const flowStore = state => state.flow;


/**
 * Función generadora que se encarga de realizar la llamada al API
 * actualizar/contacto (updateMyContacts).
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* updateMyContacts(params) {
  const { showError, normalizeHttpErrors } = params;
  const reduxUpdate = {
    successUpdate: commonActions.setUpdateMyContactsSuccess,
    errorUpdate: showError,
  };

  const apiNeeds = yield select(apisNeedsStore);

  const { clientInfo, personalContactInfo, obd } = apiNeeds;

  const {
    additionalInfo,
    contactEmail,
    contactPhone,
  } = clientInfo;

  const { activeOBD } = obd;

  const {
    contactPhone: modifiedPhone,
    email: modifiedMail,
    advertisingUse,
  } = personalContactInfo;

  let modifyContactsParams = null;

  const isPhoneModified = modifiedPhone !== contactPhone.value;
  const isEmailModified = modifiedMail !== contactEmail.value;

  /* Comprobamos los medios de contacto que ha modificado el cliente
    para actualizar los que corresponda */

  if (isPhoneModified && !isEmailModified) {
    // Si ha modificado solamente el teléfono de contacto
    const newContactPhone = {
      ...contactPhone,
      value: modifiedPhone,
    };
    modifyContactsParams = {
      reduxUpdate,
      additionalInfo,
      newContactPhone,
      normalizeHttpErrors,
    };
  } else if (!isPhoneModified && isEmailModified) {
    // Si ha modificado solamente el mail
    const newContactEmail = {
      ...contactEmail,
      value: modifiedMail,
      advertisingUse: !!advertisingUse,
    };
    modifyContactsParams = {
      reduxUpdate,
      additionalInfo,
      newContactEmail,
      normalizeHttpErrors,
    };
  } else if (isPhoneModified && isEmailModified) {
    // Si ha modificado tanto el teléfono de contacto como el mail
    const newContactEmail = {
      ...contactEmail,
      value: modifiedMail,
      advertisingUse: !!advertisingUse,
    };
    const newContactPhone = {
      ...contactPhone,
      value: modifiedPhone,
    };
    modifyContactsParams = {
      reduxUpdate,
      additionalInfo,
      newContactEmail,
      newContactPhone,
      normalizeHttpErrors,
    };
  }

  if (modifyContactsParams) {
    if (!activeOBD) {
      yield put(commonActions.setBlockedByObdStatus(true));
      return ({
        blockedByOBD: true,
      });
    }

    try {
      const response = yield call(updateMyContactsAda, modifyContactsParams);
      response.blockedByOBD = false;
      return response;
    } catch (error) {
      return error;
    }
  }
  return ({
    blockedByOBD: false,
  });
}

/**
 * Función generadora que se encarga de realizar la llamada al API
 * actualizar/contacto (updateMyContacts) para marcar el uso publicitario del email.
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* allowAdvertisingUse(params) {
  const { showError, normalizeHttpErrors } = params;
  const reduxUpdate = {
    successUpdate: commonActions.setUpdateMyContactsSuccess,
    errorUpdate: showError,
  };

  const apiNeeds = yield select(apisNeedsStore);

  const { clientInfo, personalContactInfo } = apiNeeds;

  const {
    additionalInfo,
    contactEmail,
  } = clientInfo;

  const {
    email: modifiedMail,
    advertisingUse,
  } = personalContactInfo;

  const newContactEmail = {
    ...contactEmail,
    value: modifiedMail,
    advertisingUse,
  };
  const modifyContactsParams = {
    reduxUpdate,
    additionalInfo,
    newContactEmail,
    normalizeHttpErrors,
    webServiceName: WEB_SERVICES.ALLOW_ADVERTISING_USE,
  };

  if (advertisingUse) {
    yield call(updateMyContactsAda, modifyContactsParams);
  }
}

/**
 * Función generadora que se encarga de realizar la llamada al API
 * actualizar/residencia (updatePermanentResidence).
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* updatePermanentResidence(params) {
  const { showError, normalizeHttpErrors } = params;
  const reduxUpdate = {
    successUpdate: commonActions.setUpdatePermanentResidenceSuccess,
    errorUpdate: showError,
  };

  const apiNeeds = yield select(apisNeedsStore);

  const { updatePermanentResidenceNeeds } = apiNeeds;

  const { id: permanentResidenceId } = updatePermanentResidenceNeeds;

  const updatePermanentResidenceParams = { reduxUpdate, normalizeHttpErrors, permanentResidenceId };

  yield call(updatePermanentResidenceAda, updatePermanentResidenceParams);
}

export function* getClientData(params) {
  const context = yield select(contextStore);
  const { handledErrors } = yield select(getErrorManagement);
  const flowInfo = yield select(flowStore);

  const { normalizeHttpErrors, showError } = params;
  const getClientParams = {
    reduxUpdate: {
      successUpdate: commonActions.setClientInfo,
      errorUpdate: showError,
    },
    normalizeHttpErrors,
  };

  try {
    const clientInfo = yield call(getClientDataAda, getClientParams);

    if (flowInfo?.flowJson?.blockUsersByDocType && flowInfo.flowJson.blockUsersByDocType.includes(clientInfo.docType)) {
      yield put(showError(handledErrors.BUSINESS_ERRORS.BLOCKED_USER));
      return megaMinderStatus.KO;
    }
    if (clientInfo
      && clientInfo.contactPhone && clientInfo.contactPhone.value
      && clientInfo.contactEmail && clientInfo.contactEmail.value
      && ((clientInfo.permanentResidence && clientInfo.permanentResidence.id) || context.userType === USER_TYPE.OPEN)
    ) {
      return megaMinderStatus.OK;
    }
    return megaMinderStatus.LOGICAL_ERROR;
  } catch (error) {
    const errorResponse = megaMinderStatus.KO;
    errorResponse.errorInfo = error;

    return errorResponse;
  }
}

export function* getMyDataType() {
  try {
    const clientTypeInfo = yield call(getMyDataTypeAda);
    return clientTypeInfo;
  } catch (error) {
    console.log(error);
    return null;
  }
}

export function* getClientDataForConfirmation(params) {
  const context = yield select(contextStore);

  if (context && context.userType === USER_TYPE.OPEN) {
    return megaMinderStatus.OK;
  }

  const { normalizeHttpErrors, showError } = params;
  const getClientParams = {
    reduxUpdate: {
      successUpdate: commonActions.setClientInfo,
      errorUpdate: showError,
    },
    normalizeHttpErrors,
  };

  try {
    const clientInfo = yield call(getClientDataAda, getClientParams);

    if (clientInfo
      && clientInfo.contactPhone && clientInfo.contactPhone.value
      && clientInfo.contactEmail && clientInfo.contactEmail.value
      && ((clientInfo.permanentResidence && clientInfo.permanentResidence.id) || context.userType === USER_TYPE.OPEN)
    ) {
      return megaMinderStatus.OK;
    }
    return megaMinderStatus.LOGICAL_ERROR;
  } catch (error) {
    const errorResponse = megaMinderStatus.KO;
    errorResponse.errorInfo = error;

    return errorResponse;
  }
}
